import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
import { Sparkles, Zap, Hash, Layers, Image as ImageIcon, Check, ArrowRight, Plus } from 'lucide-react';
import { motion } from 'motion/react';
import { Box, Container, Typography, Grid, Paper, Stack, AppBar, Toolbar, useTheme } from '@mui/material';

export default function Landing() {
  const theme = useTheme();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
      <AppBar position="static" color="transparent" elevation={0} sx={{ py: 1 }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Sparkles color="#3b82f6" />
            <Typography variant="h6" fontWeight="bold" letterSpacing="-0.02em" sx={{ color: '#111827' }}>
              PostPilot AI
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={3}>
            <Typography component={Link} to="#" variant="body2" sx={{ textDecoration: 'none', color: '#4b5563', '&:hover': { color: '#111827' } }}>
              Features
            </Typography>
            <Typography component={Link} to="#" variant="body2" sx={{ textDecoration: 'none', color: '#4b5563', '&:hover': { color: '#111827' } }}>
              Pricing
            </Typography>
            <Typography component={Link} to="#" variant="body2" sx={{ textDecoration: 'none', color: '#4b5563', '&:hover': { color: '#111827' } }}>
              Demo
            </Typography>
            <Button component={Link} to="/auth" state={{ mode: 'signup' }} size="sm" sx={{ bgcolor: '#3b82f6', color: 'white', '&:hover': { bgcolor: '#2563eb' }, borderRadius: 2, px: 3 }}>Start Generating</Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Box component="main" sx={{ flexGrow: 1 }}>
        {/* Hero Section */}
        <Box sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 8, md: 16 }, px: 3 }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid size={{ xs: 12, md: 6 }}>
                <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
                  <Typography variant="h2" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '3rem', md: '4.5rem' }, lineHeight: 1.1, letterSpacing: '-0.02em', color: '#111827', mb: 3 }}>
                    Create Viral<br />Instagram Posts<br />with AI
                  </Typography>
                  <Typography variant="h6" color="#4b5563" sx={{ mb: 5, maxWidth: 480, fontWeight: 400, lineHeight: 1.6 }}>
                    Generate captions, hooks, hashtags, and carousel content instantly. Scale your presence without the creative burnout.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button component={Link} to="/auth" state={{ mode: 'signup' }} size="lg" sx={{ bgcolor: '#3b82f6', color: 'white', '&:hover': { bgcolor: '#2563eb' }, borderRadius: 2, px: 4, py: 1.5, fontWeight: 'bold' }}>Start Generating</Button>
                    <Button size="lg" variant="outline" sx={{ bgcolor: 'white', color: '#111827', borderColor: '#e5e7eb', borderRadius: 2, px: 4, py: 1.5, fontWeight: 'bold', '&:hover': { bgcolor: '#f9fafb' } }}>View Demo</Button>
                  </Stack>
                </motion.div>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
                  <Box sx={{
                    height: 400,
                    width: '100%',
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #e0e7ff 0%, #fae8ff 100%)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative'
                  }}>
                    {/* Placeholder for the dashboard mockup image */}
                    <Box sx={{ width: '80%', height: '80%', bgcolor: 'rgba(255,255,255,0.8)', borderRadius: 2, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' }} />
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Features Section */}
        <Box sx={{ py: 12, px: 3 }}>
          <Container maxWidth="lg">
            <Box sx={{ mb: 8 }}>
              <Typography variant="h3" fontWeight={800} gutterBottom sx={{ color: '#111827', letterSpacing: '-0.02em', mb: 2 }}>
                Powerful AI Tools for Creators
              </Typography>
              <Typography variant="h6" color="#4b5563" sx={{ fontWeight: 400 }}>
                Everything you need to grow your Instagram presence, powered by state-of-the-art language models.
              </Typography>
            </Box>

            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 4 }}>
                <FeatureCard
                  icon={<Zap size={24} color="#3b82f6" />}
                  iconBg="#eff6ff"
                  title="AI Caption Generator"
                  description="Write engaging, brand-aligned captions in seconds tailored perfectly to your niche and audience."
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FeatureCard
                  icon={<Zap size={24} color="#8b5cf6" />}
                  iconBg="#f5f3ff"
                  title="Viral Hook Generator"
                  description="Stop the scroll with high-converting hooks designed using psychological triggers to boost engagement."
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FeatureCard
                  icon={<Hash size={24} color="#ec4899" />}
                  iconBg="#fdf2f8"
                  title="Smart Hashtag Generator"
                  description="Access a curated set of trending and low-competition hashtags to maximize your reach."
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FeatureCard
                  icon={<Layers size={24} color="#10b981" />}
                  iconBg="#ecfdf5"
                  title="Carousel Post Creator"
                  description="Structure multi-slide educational and storytelling content with automated flow logic."
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FeatureCard
                  icon={<ImageIcon size={24} color="#f59e0b" />}
                  iconBg="#fffbeb"
                  title="Image Prompt Generator"
                  description="Generate optimized prompts for Midjourney and DALL-E to create stunning visual assets."
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{
                  p: 4,
                  borderRadius: 4,
                  bgcolor: 'transparent',
                  borderStyle: 'dashed',
                  borderColor: '#cbd5e1',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: '#e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                    <Plus size={24} color="#64748b" />
                  </Box>
                  <Typography variant="body1" color="#64748b" fontWeight="medium">More features arriving weekly...</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Pricing Section */}
        <Box sx={{ py: 12, px: 3 }}>
          <Container maxWidth="md">
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography variant="h3" fontWeight={800} gutterBottom sx={{ color: '#111827', letterSpacing: '-0.02em', mb: 2 }}>
                Simple, transparent pricing
              </Typography>
              <Typography variant="h6" color="#4b5563" sx={{ fontWeight: 400 }}>
                Scale your social media strategy without breaking the bank.
              </Typography>
            </Box>

            <Grid container spacing={4} justifyContent="center" alignItems="stretch">
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper variant="outlined" sx={{ p: 5, borderRadius: 4, bgcolor: 'white', borderColor: '#e2e8f0', height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h5" fontWeight={700} color="#111827" gutterBottom>Free Plan</Typography>
                  <Typography color="#64748b" sx={{ mb: 4, fontSize: '0.95rem' }}>Perfect for exploring our tools</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 5 }}>
                    <Typography variant="h3" fontWeight={800} color="#111827">$0</Typography>
                    <Typography variant="subtitle1" color="#64748b" sx={{ ml: 1 }}>/mo</Typography>
                  </Box>

                  <Button component={Link} to="/auth" state={{ mode: 'signup' }} variant="outline" sx={{ width: '100%', mb: 5, borderRadius: 2, py: 1.5, borderColor: '#e2e8f0', color: '#111827', fontWeight: 'bold' }}>Get Started</Button>

                  <Stack spacing={2.5} sx={{ flexGrow: 1 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start"><Check color="#10b981" size={20} style={{ marginTop: 2 }} /><Typography color="#4b5563">10 posts per month</Typography></Stack>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start"><Check color="#10b981" size={20} style={{ marginTop: 2 }} /><Typography color="#4b5563">Basic Caption Generator</Typography></Stack>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start"><Check color="#10b981" size={20} style={{ marginTop: 2 }} /><Typography color="#4b5563">Standard Hashtags</Typography></Stack>
                  </Stack>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 5, borderRadius: 4, border: 2, borderColor: '#3b82f6', bgcolor: 'white', position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(59, 130, 246, 0.1)' }}>
                  <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translate(-50%, -50%)', bgcolor: '#3b82f6', color: 'white', px: 2, py: 0.5, borderRadius: 4 }}>
                    <Typography variant="caption" fontWeight="bold" letterSpacing="0.05em">MOST POPULAR</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={700} color="#3b82f6" gutterBottom>Pro Plan</Typography>
                  <Typography color="#64748b" sx={{ mb: 4, fontSize: '0.95rem' }}>For serious content creators.</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 5 }}>
                    <Typography variant="h3" fontWeight={800} color="#111827">$9</Typography>
                    <Typography variant="subtitle1" color="#64748b" sx={{ ml: 1 }}>/mo</Typography>
                  </Box>

                  <Button component={Link} to="/auth" state={{ mode: 'signup' }} variant="primary" sx={{ width: '100%', mb: 5, borderRadius: 2, py: 1.5, bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' }, fontWeight: 'bold' }}>Go Pro</Button>

                  <Stack spacing={2.5} sx={{ flexGrow: 1 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start"><Check color="#3b82f6" size={20} style={{ marginTop: 2 }} /><Typography color="#4b5563">Unlimited posts</Typography></Stack>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start"><Check color="#3b82f6" size={20} style={{ marginTop: 2 }} /><Typography color="#4b5563">Viral Hook Generator</Typography></Stack>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start"><Check color="#3b82f6" size={20} style={{ marginTop: 2 }} /><Typography color="#4b5563">Carousel Creator</Typography></Stack>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start"><Check color="#3b82f6" size={20} style={{ marginTop: 2 }} /><Typography color="#4b5563">Priority Support</Typography></Stack>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start"><Check color="#3b82f6" size={20} style={{ marginTop: 2 }} /><Typography color="#4b5563">Advanced SEO Hashtags</Typography></Stack>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box sx={{ py: 12, px: 3 }}>
          <Container maxWidth="lg">
            <Box sx={{
              bgcolor: '#4f46e5',
              borderRadius: 6,
              p: { xs: 6, md: 10 },
              textAlign: 'center',
              color: 'white',
              boxShadow: '0 20px 25px -5px rgba(79, 70, 229, 0.4)'
            }}>
              <Typography variant="h3" fontWeight={800} gutterBottom sx={{ mb: 3 }}>
                Ready to go viral?
              </Typography>
              <Typography variant="h6" sx={{ mb: 6, opacity: 0.9, maxWidth: 600, mx: 'auto', fontWeight: 400 }}>
                Join 5,000+ creators who use PostPilot AI to scale their reach and grow their audience.
              </Typography>
              <Button component={Link} to="/auth" state={{ mode: 'signup' }} size="lg" sx={{ bgcolor: 'white', color: '#4f46e5', borderRadius: 2, px: 5, py: 1.5, fontWeight: 'bold', '&:hover': { bgcolor: '#f8fafc' } }}>
                Start Generating Now
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ py: 6, bgcolor: 'white', borderTop: 1, borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Grid container spacing={8}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Sparkles color="#3b82f6" size={20} />
                <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#111827' }}>
                  PostPilot AI
                </Typography>
              </Stack>
              <Typography variant="body2" color="#64748b" sx={{ maxWidth: 250, lineHeight: 1.6 }}>
                Empowering creators with AI-driven insights and automated content generation tools for Instagram growth.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#111827', mb: 3 }}>Product</Typography>
              <Stack spacing={2}>
                <Typography component={Link} to="#" variant="body2" color="#64748b" sx={{ textDecoration: 'none', '&:hover': { color: '#3b82f6' } }}>Features</Typography>
                <Typography component={Link} to="#" variant="body2" color="#64748b" sx={{ textDecoration: 'none', '&:hover': { color: '#3b82f6' } }}>Pricing</Typography>
                <Typography component={Link} to="#" variant="body2" color="#64748b" sx={{ textDecoration: 'none', '&:hover': { color: '#3b82f6' } }}>Showcase</Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#111827', mb: 3 }}>Legal</Typography>
              <Stack spacing={2}>
                <Typography component={Link} to="#" variant="body2" color="#64748b" sx={{ textDecoration: 'none', '&:hover': { color: '#3b82f6' } }}>Privacy Policy</Typography>
                <Typography component={Link} to="#" variant="body2" color="#64748b" sx={{ textDecoration: 'none', '&:hover': { color: '#3b82f6' } }}>Terms of Service</Typography>
                <Typography component={Link} to="#" variant="body2" color="#64748b" sx={{ textDecoration: 'none', '&:hover': { color: '#3b82f6' } }}>Cookie Policy</Typography>
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ mt: 8, pt: 4, borderTop: 1, borderColor: '#f1f5f9', textAlign: 'center' }}>
            <Typography variant="caption" color="#94a3b8">
              © {new Date().getFullYear()} PostPilot AI. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box >
  );
}

function FeatureCard({ icon, iconBg, title, description }: { icon: React.ReactNode, iconBg: string, title: string, description: string }) {
  return (
    <Paper elevation={0} sx={{ p: 4, borderRadius: 4, bgcolor: 'white', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
        {icon}
      </Box>
      <Typography variant="h6" fontWeight={700} color="#111827" gutterBottom>{title}</Typography>
      <Typography variant="body2" color="#64748b" sx={{ lineHeight: 1.6 }}>{description}</Typography>
    </Paper>
  );
}
